import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './TableList.css';
import './EventFiles.css';
import {
  FileText,
  Timer,
  Trophy,
  Shuffle,
  Radio,
  Compass,
  X,
  ExternalLink,
  CalendarDays,
  MapPin,
  Tag,
  History,
  ArrowLeft
} from 'lucide-react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faMoneyBillWave, faFilePdf } from '@fortawesome/free-solid-svg-icons';

import Footer from './Footer';

const API_URL = 'https://www.fro.ro/api/events/public_events.php';

const toDateOnly = (dateStr) => {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const isCurrentEvent = (event, today = new Date()) => {
  const now = new Date(today);
  now.setHours(0, 0, 0, 0);

  const start = addDays(toDateOnly(event.start_date), -1);
  const end = toDateOnly(event.end_date || event.start_date);
  const displayEnd = addDays(end, 1);

  return now >= start && now <= displayEnd;
};

const isPastEvent = (event, today = new Date()) => {
  const now = new Date(today);
  now.setHours(0, 0, 0, 0);

  const end = toDateOnly(event.end_date || event.start_date);
  const displayEnd = addDays(end, 1);

  return now > displayEnd;
};

const isFutureEvent = (event, today = new Date()) => {
  return !isCurrentEvent(event, today) && !isPastEvent(event, today);
};

export default function PublicEvents() {
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [events, setEvents] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date-asc');
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [exportYear, setExportYear] = useState('');

  const MONTHS = [
    'Ianuarie',
    'Februarie',
    'Martie',
    'Aprilie',
    'Mai',
    'Iunie',
    'Iulie',
    'August',
    'Septembrie',
    'Octombrie',
    'Noiembrie',
    'Decembrie'
  ];

  const openEventDetails = (event) => {
    if (!event?.id) return;
    navigate(`/publicevents/${event.id}`);
  };

  const closeEventDetails = () => {
    navigate('/publicevents');
  };

  const fetchEvents = async (clubName = '') => {
    setLoading(true);

    let url = API_URL;

    if (clubName) {
      url += `?club=${encodeURIComponent(clubName)}`;
    }

    try {
      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        setEvents(data.events);

        if (!clubName) {
          const uniqueClubs = [...new Set(data.events.map(e => e.club_name))];
          setClubs(uniqueClubs);
        }
      } else {
        setEvents([]);
      }
    } catch (err) {
      console.error('Eroare la încărcare evenimente:', err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (!eventId) {
      setSelectedEvent(null);
      return;
    }

    if (events.length === 0) return;

    const foundEvent = events.find(e => String(e.id) === String(eventId));

    if (foundEvent) {
      setSelectedEvent(foundEvent);
    } else {
      setSelectedEvent(null);
    }
  }, [eventId, events]);

  useEffect(() => {
    if (!selectedEvent) return;

    const onKey = (e) => {
      if (e.key === 'Escape') {
        closeEventDetails();
      }
    };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [selectedEvent]);

  const handleClubChange = (e) => {
    const club = e.target.value;
    setSelectedClub(club);
    fetchEvents(club);
  };

  const today = new Date();

  const currentEvents = events.filter(e => isCurrentEvent(e, today));
  const futureEvents = events.filter(e => isFutureEvent(e, today));
  const pastEvents = events.filter(e => isPastEvent(e, today));

  const filterList = (list) =>
    list.filter(e => {
      const eventMonth = new Date(e.start_date).getMonth();

      return (
        e.name.toLowerCase().includes(search.toLowerCase()) &&
        (selectedClub === '' || e.club_name === selectedClub) &&
        (selectedMonth === '' || Number(selectedMonth) === eventMonth)
      );
    });

  const filteredCurrent = filterList(currentEvents);
  const filteredFuture = filterList(futureEvents);
  const filteredPast = filterList(pastEvents);

  const sortEvents = (list) => {
    return [...list].sort((a, b) => {
      const monthA = new Date(a.start_date).getMonth();
      const monthB = new Date(b.start_date).getMonth();

      switch (sortBy) {
        case 'date-asc':
          return new Date(a.start_date) - new Date(b.start_date);

        case 'date-desc':
          return new Date(b.start_date) - new Date(a.start_date);

        case 'club':
          return a.club_name.localeCompare(b.club_name);

        case 'month':
          return monthA - monthB;

        default:
          return 0;
      }
    });
  };

  const sortedCurrent = sortEvents(filteredCurrent);
  const sortedFuture = sortEvents(filteredFuture);

  const sortedPast = [...filteredPast].sort(
    (a, b) => new Date(b.start_date) - new Date(a.start_date)
  );

  // Ani disponibili pentru exportul calendarului.
  // Sunt generați automat din evenimentele existente.
  const availableYears = [
    ...new Set(
      events
        .filter(e => e.start_date)
        .map(e => Number(String(e.start_date).substring(0, 4)))
        .filter(year => !Number.isNaN(year))
    )
  ].sort((a, b) => a - b);

  const isValidLink = (val) => val && val.trim() !== '';

  const EventCard = ({ e }) => {
    const canRegister = isFutureEvent(e);

    return (
      <div className="event-card" onClick={() => openEventDetails(e)}>
        <div className="event-image">
          {e.poster_image ? (
            <img
              src={e.poster_image}
              alt={e.name}
              style={{
                objectFit: 'cover',
                width: '100%',
                height: '180px',
                borderRadius: '8px'
              }}
            />
          ) : (
            <div className="no-image">Fără imagine</div>
          )}
        </div>

        <div className="event-content">
          <span className="event-tag">
            <Tag size={14} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
            {e.club_name}
          </span>

          <span className="event-date">
            <CalendarDays size={14} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
            {e.start_date}
          </span>

          <h3 className="event-title">{e.name}</h3>

          <p className="event-location">
            <MapPin size={14} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
            {e.location}
          </p>

          <div style={{ marginTop: 8 }}>
            {canRegister && isValidLink(e.registration_url) && (
              <>
                <a
                  href={e.registration_url}
                  target="_blank"
                  rel="noreferrer"
                  className="event-link"
                  onClick={(ev) => ev.stopPropagation()}
                  style={{
                    marginRight: 6,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  Înscrie-te aici! <ExternalLink size={14} />
                </a>

                <span style={{ margin: '0 6px', color: '#999' }}>|</span>
              </>
            )}

            <a
              href={`/app/publicevents/${e.id}`}
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                openEventDetails(e);
              }}
              className="event-link"
            >
              Detalii eveniment
            </a>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="eventlist-container">
        <div className="container-title">
          <button onClick={() => navigate('/')} className="back-button">
            <ArrowLeft size={18} strokeWidth={2.2} />
          </button>

          <h1 className="title">Evenimente Sportive</h1>
        </div>

        <div className="event-info-boxes">
          <div className="event-info-card">
            <div className="event-info-header">
              <FontAwesomeIcon icon={faCircleInfo} className="event-info-icon info" />
            </div>

            <a href="/app/docs/precizari_fro_2026.pdf" target="_blank" rel="noreferrer">
              Precizări generale competiții
            </a>
          </div>

          <div className="event-info-card">
            <div className="event-info-header">
              <FontAwesomeIcon icon={faMoneyBillWave} className="event-info-icon fees" />
            </div>

            <a href="/app/docs/taxe_vize_2026.pdf" target="_blank" rel="noreferrer">
              Taxe și vize
            </a>
          </div>

          <div className="event-info-card">
            <div className="event-info-header">
              <FontAwesomeIcon
                icon={faFilePdf}
                className="event-info-icon pdf"
              />
            </div>

            <select
              value={exportYear}
              onChange={(e) => setExportYear(e.target.value)}
              style={{
                marginBottom: '8px',
                padding: '6px 10px',
                borderRadius: '6px',
                border: '1px solid #ccc',
                backgroundColor: '#fff',
                cursor: 'pointer',
                width: '100%',
                maxWidth: '160px'
              }}
            >
              <option value="">Toți anii</option>

              {availableYears.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            <a
              href={
                exportYear
                  ? `/api/events/export_calendar.php?year=${encodeURIComponent(exportYear)}`
                  : '/api/events/export_calendar.php'
              }
              target="_blank"
              rel="noreferrer"
            >
              Export calendar
            </a>
          </div>
        </div>
      </div>

      <div className="table-list-header" style={{ marginBottom: 20 }}>
        <div className="sort-bar">
          <label htmlFor="sort-select">Sortează:</label>

          <select
            id="sort-select"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
          >
            <option value="date-asc">Dată crescător</option>
            <option value="date-desc">Dată descrescător</option>
            <option value="club">Club</option>
            <option value="month">Lună (ianuarie → decembrie)</option>
          </select>
        </div>

        <div className="sort-bar">
          <label htmlFor="month-select">Lună:</label>

          <select
            id="month-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="">Toate lunile</option>

            {MONTHS.map((m, index) => (
              <option key={index} value={index}>
                {m}
              </option>
            ))}
          </select>
        </div>

        <div className="sort-bar">
          <label htmlFor="club-select">Club:</label>

          <select
            id="club-select"
            value={selectedClub}
            onChange={handleClubChange}
          >
            <option value="">Toate cluburile</option>

            {clubs.map(club => (
              <option key={club} value={club}>
                {club}
              </option>
            ))}
          </select>
        </div>

        <input
          type="text"
          className="search-bar"
          placeholder="Caută eveniment..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <section style={{ marginBottom: 40 }}>
        <h2 className="section-title">
          <Timer size={20} color="#22c55e" style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Evenimente în curs
        </h2>

        {loading ? (
          <div className="loading">Se încarcă evenimentele...</div>
        ) : sortedCurrent.length === 0 ? (
          <div className="no-results">Nu există evenimente în curs.</div>
        ) : (
          <div className="events-grid">
            {sortedCurrent.map((e) => (
              <EventCard key={e.id} e={e} />
            ))}
          </div>
        )}
      </section>

      <section style={{ marginBottom: 40 }}>
        <h2 className="section-title">
          <CalendarDays size={20} color="#ffb625" style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Evenimente viitoare
        </h2>

        {loading ? (
          <div className="loading">Se încarcă evenimentele...</div>
        ) : sortedFuture.length === 0 ? (
          <div className="no-results">Nu există evenimente viitoare.</div>
        ) : (
          <div className="events-grid">
            {sortedFuture.map((e) => (
              <EventCard key={e.id} e={e} />
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="section-title">
          <History size={20} color="#888" style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Evenimente anterioare
        </h2>

        {loading ? (
          <div className="loading">Se încarcă evenimentele...</div>
        ) : sortedPast.length === 0 ? (
          <div className="no-results">Nu există evenimente anterioare.</div>
        ) : (
          <div className="events-grid">
            {sortedPast.map((e) => (
              <EventCard key={e.id} e={e} />
            ))}
          </div>
        )}
      </section>

      {selectedEvent && (
        <div className="modal-backdrop pe-backdrop" onClick={closeEventDetails}>
          <AnimatedModal
            selectedEvent={selectedEvent}
            onClose={closeEventDetails}
          />
        </div>
      )}

      <Footer />
    </>
  );
}

/* ---------------------- Modal + Tabs ---------------------- */

function AnimatedModal({ selectedEvent, onClose }) {
  const [tabState, setTabState] = useState('overview');

  return (
    <div
      className={`modal-box pe-modal tab-${tabState}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="pe-head">
        <h3 className="pe-title">{selectedEvent.name}</h3>

        <button className="pe-close" onClick={onClose}>
          <X size={20} />
        </button>
      </div>

      <ModalTabs
        selectedEvent={selectedEvent}
        onTabChange={setTabState}
      />
    </div>
  );
}

function ModalTabs({ selectedEvent, onTabChange }) {
  const [tab, setTab] = useState('overview');

  const isValidLink = (v) => v && v.trim() !== '';

  const countOf = (prefix, max = 6) =>
    Array.from({ length: max }).filter((_, i) => !!selectedEvent[`${prefix}${i + 1}_url`]).length;

  const hasAnyFiles =
    countOf('bulletin_') +
    countOf('start_list_') +
    countOf('result_file_') +
    countOf('split_file_') >
    0;

  const hasLive = Array.from({ length: 6 }).some((_, i) =>
    isValidLink(selectedEvent[`live_result_${i + 1}`])
  );

  const hasLivelox = Array.from({ length: 6 }).some((_, i) =>
    isValidLink(selectedEvent[`livelox_url_${i + 1}`])
  );

  const activeClass = (name) => (tab === name ? 'is-active' : '');

  const canRegister = isFutureEvent(selectedEvent);

  const eventShareLink = `${window.location.origin}/app/publicevents/${selectedEvent.id}`;


  return (
    <div className="pe-body">
      <div className="pe-tabs">
        <button
          className={`pe-tab ${tab === 'overview' ? 'is-active' : ''}`}
          onClick={() => {
            setTab('overview');
            onTabChange('overview');
          }}
        >
          Prezentare
        </button>

        <button
          className={`pe-tab ${tab === 'files' ? 'is-active' : ''}`}
          onClick={() => {
            setTab('files');
            onTabChange('files');
          }}
        >
          Fișiere{' '}

          {hasAnyFiles ? (
            <span className="badge">
              {countOf('bulletin_') +
                countOf('start_list_') +
                countOf('result_file_') +
                countOf('split_file_')}
            </span>
          ) : null}
        </button>

        <button
          className={`pe-tab ${tab === 'live' ? 'is-active' : ''}`}
          onClick={() => {
            setTab('live');
            onTabChange('live');
          }}
        >
          Live & Livelox {(hasLive || hasLivelox) ? <span className="badge">•</span> : null}
        </button>
      </div>

      <div className={`tab-content ${activeClass('overview')}`}>
        <div className="pe-hero pe-hero--grid">
          <div className="pe-poster pe-poster--fill">
            {selectedEvent.poster_image ? (
              <img src={selectedEvent.poster_image} alt={selectedEvent.name} />
            ) : (
              <div className="pe-noimg">Fără imagine</div>
            )}
          </div>

          <div className="pe-overview">
            <ul className="pe-kv">
              <li>
                <span className="kv-icon">
                  <Tag size={16} />
                </span>

                <div className="kv-text">
                  <span className="kv-label">Club</span>
                  <span className="kv-value">{selectedEvent.club_name}</span>
                </div>
              </li>

              <li>
                <span className="kv-icon">
                  <CalendarDays size={16} />
                </span>

                <div className="kv-text">
                  <span className="kv-label">Perioadă</span>

                  <span className="kv-value">
                    {selectedEvent.start_date} → {selectedEvent.end_date || selectedEvent.start_date}
                  </span>
                </div>
              </li>

              <li>
                <span className="kv-icon">
                  <MapPin size={16} />
                </span>

                <div className="kv-text">
                  <span className="kv-label">Locație</span>
                  <span className="kv-value">{selectedEvent.location}</span>
                </div>
              </li>
            </ul>


            {canRegister && isValidLink(selectedEvent.registration_url) && (
              <div className="pe-cta">
                <a
                  className="btn btn-primary pe-cta-btn"
                  href={selectedEvent.registration_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Înscrie-te la eveniment <ExternalLink size={16} />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`tab-content ${activeClass('files')}`}>
        {hasAnyFiles ? (
          <div className="pe-sections pe-files-grid">
            {['bulletin_', 'start_list_', 'result_file_', 'split_file_'].map((type, idx) => {
              const icons = [FileText, Timer, Trophy, Shuffle];

              const titles = [
                'Invitații & Buletine',
                'Liste de start',
                'Rezultate finale',
                'Rezultate split'
              ];

              const Icon = icons[idx];
              const title = titles[idx];

              if (countOf(type) === 0) return null;

              return (
                <section key={type} className="pe-card">
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Icon size={18} /> {title}
                  </h4>

                  <div className="chips">
                    {[1, 2, 3, 4, 5, 6].map(i =>
                      selectedEvent[`${type}${i}_url`] ? (
                        <a
                          key={i}
                          className="chip-link"
                          href={selectedEvent[`${type}${i}_url`]}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {selectedEvent[`${type}${i}_name`]}
                        </a>
                      ) : null
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        ) : (
          <div className="pe-empty-block">
            <div className="pe-empty-icon">
              <FileText size={36} strokeWidth={1.5} />
            </div>

            <div className="pe-empty-text">
              <strong>Niciun fișier disponibil momentan</strong>
              <p>Organizatorii vor publica documentele aici imediat ce vor fi gata.</p>
            </div>
          </div>
        )}
      </div>

      <div className={`tab-content ${activeClass('live')}`}>
        {(hasLive || hasLivelox) ? (
          <div className="pe-sections">
            {hasLive && (
              <section className="pe-card">
                <h4 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Radio size={18} /> Rezultate live
                </h4>

                <div className="chips">
                  {[1, 2, 3, 4, 5, 6].map(i =>
                    isValidLink(selectedEvent[`live_result_${i}`]) ? (
                      <a
                        key={i}
                        className="chip-link"
                        href={selectedEvent[`live_result_${i}`]}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Live {i}
                      </a>
                    ) : null
                  )}
                </div>
              </section>
            )}

            {hasLivelox && (
              <section className="pe-card">
                <h4 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Compass size={18} /> Livelox
                </h4>

                <div className="chips">
                  {[1, 2, 3, 4, 5, 6].map(i =>
                    isValidLink(selectedEvent[`livelox_url_${i}`]) ? (
                      <a
                        key={i}
                        className="chip-link"
                        href={selectedEvent[`livelox_url_${i}`]}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Livelox {i}
                      </a>
                    ) : null
                  )}
                </div>
              </section>
            )}
          </div>
        ) : (
          <div className="pe-empty-block">
            <div className="pe-empty-icon">
              <Radio size={36} strokeWidth={1.5} />
            </div>

            <div className="pe-empty-text">
              <strong>Nu există încă linkuri Live sau Livelox</strong>
              <p>Revino în ziua competiției pentru actualizări în timp real.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}